import { useCallback, useEffect } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import { api } from "../../../singleton/axios";
import { savedScreensQueryKey, screenNewMatchesPrefix } from "./queryKeys";
import { ScreenerRequestError } from "./useScreenerRuns";
import type {
  SavedScreensResponse,
  ScreenAlertsResponse,
  ScreenNewMatches,
  ScreenSort,
  ScreenerApiError,
} from "../types";
import {
  SCREENER_READ_TIMEOUT_MS,
  screenerRetry,
  screenerRetryDelay,
} from "./screenerRetry";

const toRequestError = (error: unknown): ScreenerRequestError => {
  const response = (
    error as { response?: { status?: number; data?: ScreenerApiError } }
  )?.response;
  return new ScreenerRequestError(response?.status ?? 0, response?.data);
};

/**
 * Alerts on or off for one saved screen (Pro). The answer is the truthful
 * status once the baseline has committed; a 422 (too broad, or filters that
 * no longer compile) changes nothing and comes back as the error's `body`.
 */
export function useSetScreenAlerts() {
  const queryClient = useQueryClient();
  return useMutation<
    ScreenAlertsResponse,
    ScreenerRequestError,
    { id: string; enabled: boolean }
  >({
    mutationFn: async ({ id, enabled }) => {
      try {
        const { data } = await api.put<ScreenAlertsResponse>(
          `/screener/screens/${encodeURIComponent(id)}/alerts`,
          { enabled },
        );
        return data;
      } catch (error) {
        throw toRequestError(error);
      }
    },
    onSuccess: (result, { id }) => {
      queryClient.setQueryData<SavedScreensResponse>(
        savedScreensQueryKey,
        (current) =>
          current && {
            ...current,
            screens: current.screens.map((screen) =>
              screen.id === id
                ? { ...screen, alertStatus: result.alertStatus }
                : screen,
            ),
          },
      );
      void queryClient.invalidateQueries({ queryKey: savedScreensQueryKey });
    },
  });
}

export interface NewMatchesScope {
  /** The day an email or inbox link names: shown whatever its state. */
  d: string | null;
  /** The table's visible columns, so the pinned rows carry them. */
  columns: readonly string[];
  /** The version the results show: a newer one asks again. */
  version: number | undefined;
  /** The table's order, which the names are listed in (none: market cap, high to low). */
  sort: ScreenSort | undefined;
}

// Where the link's day sits in screenNewMatchesQueryKey.
const DAY_IN_KEY = 3;

/** The same read without the link's day: what the page reads once it drops it. */
export const withoutLinkDay = (key: QueryKey): QueryKey => [
  ...key.slice(0, DAY_IN_KEY),
  "",
  ...key.slice(DAY_IN_KEY + 1),
];

/** `column:direction,...` as the API takes it; no sort is the table's default. */
const sortParam = (sort: ScreenSort | undefined): string =>
  (sort && sort.length > 0
    ? sort
    : [{ column: "market_cap_usd", direction: "desc" }]
  )
    .map((key) => `${key.column}:${key.direction}`)
    .join(",");

export const screenNewMatchesQueryKey = (
  screenId: string,
  scope: NewMatchesScope,
) =>
  [
    ...screenNewMatchesPrefix(screenId),
    scope.d ?? "",
    [...scope.columns].sort().join(","),
    scope.version ?? 0,
    sortParam(scope.sort),
  ] as const;

/** A page of names, and the version the first was read at, which every later page asks for. */
interface NewMatchesPageParam {
  page: number;
  version: number | null;
}

const FIRST_PAGE: NewMatchesPageParam = { page: 0, version: null };

/** Every page of a screen's new matches read so far, as the cache keeps them. */
export type NewMatchesPages = InfiniteData<
  ScreenNewMatches,
  NewMatchesPageParam
>;

// Names read while the read model was busy carry no figures: ask again.
const FIGURES_RETRY_MS = 60_000;

export interface ScreenNewMatchesRead {
  /** Every page read so far, first first. */
  pages: ScreenNewMatches[] | undefined;
  isPending: boolean;
  /** The previous scope's names, kept on screen while this one loads. */
  isPlaceholderData: boolean;
  /** The pages read are being read again (opening, a return to the tab, a restart). */
  isRefetching: boolean;
  error: ScreenerRequestError | null;
  retry: () => void;
  /** More names than the pages read so far. */
  hasMore: boolean;
  loadingMore: boolean;
  /** The next page failed: it is asked for again only on request. */
  moreFailed: boolean;
  loadMore: () => void;
  /** Changes whenever a read settles: a page, an error, a read of the pages again. */
  settled: string;
}

/**
 * A saved screen's new matches, pinned above its results: its alerts since
 * it was marked as seen, inside seven days, plus the day a link names, each
 * name with its row, a page at a time in the table's order. Free: it never
 * runs the screen. Opening the screen reads them afresh, and so does a
 * return to the tab or the app, since another device may have marked them
 * as seen. A later page read after a publication starts the list again.
 */
export function useScreenNewMatches(
  screenId: string | null,
  scope: NewMatchesScope,
  enabled = true,
): ScreenNewMatchesRead {
  const queryClient = useQueryClient();
  const query = useInfiniteQuery<
    ScreenNewMatches,
    ScreenerRequestError,
    NewMatchesPages,
    ReturnType<typeof screenNewMatchesQueryKey>,
    NewMatchesPageParam
  >({
    queryKey: screenNewMatchesQueryKey(screenId ?? "", scope),
    queryFn: async ({ pageParam, signal }) => {
      let data: ScreenNewMatches;
      const later = pageParam.page > 0;
      try {
        // Aborted when Mark as seen cancels it, so a stale answer is never
        // kept under any key.
        ({ data } = await api.get<ScreenNewMatches>(
          `/screener/screens/${encodeURIComponent(screenId ?? "")}/new-matches`,
          {
            params: {
              columns:
                scope.columns.length > 0 ? scope.columns.join(",") : undefined,
              d: scope.d ?? undefined,
              sort: sortParam(scope.sort),
              page: later ? pageParam.page : undefined,
              version: later ? (pageParam.version ?? undefined) : undefined,
            },
            signal,
            timeout: SCREENER_READ_TIMEOUT_MS,
          },
        ));
      } catch (error) {
        throw toRequestError(error);
      }
      // Kept under the version it answered at as well, so the results
      // adopting that version never ask again.
      if (!later && data.version !== null && data.version !== scope.version)
        queryClient.setQueryData<NewMatchesPages>(
          screenNewMatchesQueryKey(screenId ?? "", {
            ...scope,
            version: data.version,
          }),
          { pages: [data], pageParams: [FIRST_PAGE] },
        );
      return data;
    },
    initialPageParam: FIRST_PAGE,
    // Names read without figures (a busy read model) are in alert order, not
    // the table's: nothing pages on from them until they are read again.
    getNextPageParam: (last, pages) =>
      last.more > 0 && pages[0].version !== null
        ? { page: last.page + 1, version: pages[0].version }
        : undefined,
    enabled: enabled && screenId !== null,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    // A new scope keeps the names on screen until its own answer lands.
    placeholderData: keepPreviousData,
    refetchInterval: (current) =>
      current.state.data?.pages[0]?.version === null &&
      current.state.data.pages[0].matches.length > 0
        ? FIGURES_RETRY_MS
        : false,
    retry: screenerRetry,
    retryDelay: screenerRetryDelay,
  });
  const { refetch, fetchNextPage } = query;
  const retry = useCallback(() => void refetch(), [refetch]);
  // Never cancels a read of the pages already shown: that read is what
  // brings them up to date, and the next page asks at its version.
  const loadMore = useCallback(
    () => void fetchNextPage({ cancelRefetch: false }),
    [fetchNextPage],
  );
  // A publication landed between pages: the list starts again at its version.
  const versionMoved =
    query.isFetchNextPageError &&
    query.error?.body?.error === "VERSION_CHANGED";
  useEffect(() => {
    if (versionMoved) void refetch();
  }, [versionMoved, refetch]);
  return {
    pages: query.data?.pages,
    isPending: query.isPending,
    isPlaceholderData: query.isPlaceholderData,
    isRefetching: query.isRefetching,
    error: query.error,
    retry,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    moreFailed: query.isFetchNextPageError && !versionMoved,
    loadMore,
    settled: `${query.data?.pages.length ?? 0}:${query.errorUpdateCount}:${query.dataUpdatedAt}`,
  };
}

/**
 * Marks the screen's alerts read up to `d`, which also clears its dot and
 * skips their unsent email. The inbox and the badge refresh with it.
 */
export function useMarkScreenAlertsSeen() {
  const queryClient = useQueryClient();
  return useMutation<{ marked: number }, Error, { id: string; date: string }>({
    mutationFn: async ({ id, date }) => {
      const { data } = await api.post<{ marked: number }>(
        `/screener/screens/${encodeURIComponent(id)}/alerts/seen`,
        { d: date },
      );
      return data;
    },
    onSuccess: ({ marked }) => {
      if (marked === 0) return;
      void queryClient.invalidateQueries({ queryKey: savedScreensQueryKey });
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
